import { useCallback, useEffect, useState } from "react";

import { ConnectionState } from "lib/consts/ARC/connection";
import { useARC } from "providers/ARC/ARCProvider";
import { useMap } from "providers/navigation/MapProvider";
import { useNavigateToPose } from "providers/navigation/NavigateToPoseProvider";

const yawFromQuaternion = (orientation: {
    x?: number;
    y?: number;
    z?: number;
    w?: number;
} | null | undefined): number => {
    if (!orientation) return 0;
    const x = Number(orientation.x) || 0;
    const y = Number(orientation.y) || 0;
    const z = Number(orientation.z) || 0;
    const w = Number(orientation.w) || 1;
    return Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
};

const formatCoord = (value: number) =>
    Number.isFinite(value) ? value.toFixed(3) : "";

type FieldProps = {
    id: string;
    label: string;
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
};

const CoordField = ({ id, label, value, onChange, placeholder }: FieldProps) => (
    <label className="flex min-w-0 flex-col gap-1" htmlFor={id}>
        <span className="text-[11px] font-medium uppercase tracking-wide text-black40">
            {label}
        </span>
        <input
            id={id}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            spellCheck={false}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-r5 border border-black70 bg-black100 px-2 py-1.5 font-mono text-[12px] text-white outline-none placeholder:text-black50 focus:border-primary2"
        />
    </label>
);

/**
 * Developer Action Panel: issue ROS commands from the nav logs popup.
 * First capability: NavigateToPose goal issuer.
 */
const DevNavActionPanel: React.FC = () => {
    const { connectionState } = useARC();
    const { robotPose } = useMap();
    const {
        mapNavigationActive,
        showNavigateToPoseBanner,
        navigationStage,
        distanceRemaining,
        startNavigateToPoseFromMap,
        cancelNavigateToPose,
    } = useNavigateToPose();

    const [xText, setXText] = useState("");
    const [yText, setYText] = useState("");
    const [yawDegText, setYawDegText] = useState("0");
    const [formError, setFormError] = useState<string | null>(null);

    const connected = connectionState === ConnectionState.Connected;

    const fillFromRobotPose = useCallback(() => {
        if (!robotPose?.position) {
            setFormError("No /robot_pose yet");
            return;
        }
        const x = Number(robotPose.position.x);
        const y = Number(robotPose.position.y);
        const yaw = yawFromQuaternion(robotPose.orientation);
        setXText(formatCoord(x));
        setYText(formatCoord(y));
        setYawDegText(formatCoord((yaw * 180) / Math.PI));
        setFormError(null);
    }, [robotPose]);

    useEffect(() => {
        // Prefill once when pose first becomes available and fields are empty.
        if (xText || yText) return;
        if (!robotPose?.position) return;
        fillFromRobotPose();
        // Intentionally only when pose arrives while fields empty.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [robotPose]);

    const sendGoal = useCallback(() => {
        const x = Number(xText);
        const y = Number(yText);
        const yawDeg = Number(yawDegText);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            setFormError("Enter valid numeric x and y");
            return;
        }
        if (!Number.isFinite(yawDeg)) {
            setFormError("Enter a valid yaw (degrees)");
            return;
        }
        if (!connected) {
            setFormError("Control source not connected");
            return;
        }
        setFormError(null);
        startNavigateToPoseFromMap({
            x,
            y,
            theta: (yawDeg * Math.PI) / 180,
        });
    }, [connected, startNavigateToPoseFromMap, xText, yText, yawDegText]);

    const statusLine = showNavigateToPoseBanner
        ? [
              mapNavigationActive ? "map goal active" : "nav active",
              navigationStage || null,
              distanceRemaining != null
                  ? `${distanceRemaining.toFixed(2)} m left`
                  : null,
          ]
              .filter(Boolean)
              .join(" · ")
        : "idle";

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-black100 p-3">
            <div>
                <p className="text-h5 font-semibold text-white">Navigate to pose</p>
                <p className="mt-0.5 text-[11px] text-black40">
                    Issues <span className="font-mono text-black50">/navigate_to_pose</span>{" "}
                    in the map frame.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
                <CoordField id="dev-nav-goal-x" label="x (m)" value={xText} onChange={setXText} />
                <CoordField id="dev-nav-goal-y" label="y (m)" value={yText} onChange={setYText} />
                <CoordField
                    id="dev-nav-goal-yaw"
                    label="yaw (°)"
                    value={yawDegText}
                    onChange={setYawDegText}
                    placeholder="0"
                />
            </div>

            {formError ? <p className="text-h5 text-warning">{formError}</p> : null}

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    disabled={!connected}
                    onClick={sendGoal}
                    className="rounded-r5 border border-primary2/60 bg-primary2/20 px-3 py-1.5 text-h5 font-semibold text-white hover:bg-primary2/30 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Send goal
                </button>
                <button
                    type="button"
                    disabled={!showNavigateToPoseBanner}
                    onClick={cancelNavigateToPose}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1.5 text-h5 text-white hover:bg-black80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={!robotPose?.position}
                    onClick={fillFromRobotPose}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1.5 text-h5 text-white hover:bg-black80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Use robot pose
                </button>
            </div>

            <p className="font-mono text-[11px] text-black40">
                Status: <span className="text-black50">{statusLine}</span>
                {!connected ? (
                    <span className="ml-2 text-warning">disconnected</span>
                ) : null}
            </p>
        </div>
    );
};

export default DevNavActionPanel;
